import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "hooks/useApi";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useNotification } from "./useNotification";
import { useRosterAndStaffQueryDataUpdater } from "./useRosterAndStaffQueryDataUpdater";

type Props = {
  entityId: string;
  seasonId: string;
  fixtureId: string;
};

export const useFetchFixtureRoster = ({ entityId, seasonId, fixtureId }: Props) => {
  const callApi = useApi();
  const { sport, organizationId } = useParams();

  return useQuery({
    queryKey: ["fixtureRoster", sport, organizationId, entityId, seasonId, fixtureId],
    queryFn: () =>
      callApi(`/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/fixtures/${fixtureId}/roster`, {
        defaultErrorMessage: "Can't fetch fixture roster",
      }),
  });
};

export const useFetchPublishedFixtureRoster = ({
  entityId,
  seasonId,
  fixtureId,
  enabled,
}: Props & { enabled: boolean }) => {
  const callApi = useApi();
  const { sport, organizationId } = useParams();

  return useQuery({
    queryKey: ["fixtureRoster", "published", sport, organizationId, entityId, seasonId, fixtureId],
    queryFn: () =>
      callApi(
        `/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/fixtures/${fixtureId}/roster/published`,
        {
          defaultErrorMessage: "Can't fetch published fixture roster",
        }
      ),
    enabled,
  });
};

export const useDeleteFixtureRosterPlayers = () => {
  const { sport, organizationId } = useParams();
  const { formatMessage } = useIntl();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();
  const { replaceRecords } = useRosterAndStaffQueryDataUpdater();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ entityId, seasonId, fixtureId, data }: Props & { data: any }) => {
      return callApi(
        `/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/fixtures/${fixtureId}/roster`,
        {
          method: "DELETE",
          body: data,
        }
      );
    },
    onSuccess: (response: any, { entityId, seasonId, fixtureId }: Props & { data: any }) => {
      replaceRecords(["fixtureRoster", sport, organizationId, entityId, seasonId, fixtureId], response);
      queryClient.invalidateQueries({
        queryKey: ["fixture", sport, organizationId, entityId, seasonId, fixtureId],
      });
      showSuccessNotification(formatMessage({ id: "notification.rosterPlayer.updated" }));
    },
  });
};

export const useAddFixtureRosterPlayers = () => {
  const { sport, organizationId } = useParams();
  const { formatMessage } = useIntl();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();
  const { replaceRecords } = useRosterAndStaffQueryDataUpdater();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ entityId, seasonId, fixtureId, data }: Props & { data: any }) => {
      return callApi(
        `/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/fixtures/${fixtureId}/roster`,
        {
          method: "POST",
          body: data,
        }
      );
    },
    onSuccess: (response: any, { entityId, seasonId, fixtureId }: Props & { data: any }) => {
      replaceRecords(["fixtureRoster", sport, organizationId, entityId, seasonId, fixtureId], response);
      queryClient.invalidateQueries({
        queryKey: ["fixture", sport, organizationId, entityId, seasonId, fixtureId],
      });
      showSuccessNotification(formatMessage({ id: "notification.rosterPlayer.updated" }));
    },
    onError: (_: any, { entityId, seasonId, fixtureId }: Props & { data: any }) => {
      queryClient.invalidateQueries({
        queryKey: ["fixtureRoster", sport, organizationId, entityId, seasonId, fixtureId],
      });
    },
  });
};

export const useUpdateFixtureRosterPlayerInternal = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId } = useParams();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();
  const { replaceRecords } = useRosterAndStaffQueryDataUpdater();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ entityId, seasonId, fixtureId, personId, data }: Props & { personId: string; data: any }) =>
      callApi(
        `/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/fixtures/${fixtureId}/roster/${personId}`,
        {
          method: "PUT",
          body: data,
        }
      ),
    onSuccess: (response: any, { entityId, seasonId, fixtureId }: Props & { personId: string; data: any }) => {
      replaceRecords(["fixtureRoster", sport, organizationId, entityId, seasonId, fixtureId], response);
      queryClient.invalidateQueries({
        queryKey: ["fixture", sport, organizationId, entityId, seasonId, fixtureId],
      });
      showSuccessNotification(formatMessage({ id: "notification.rosterPlayer.updated" }));
    },
    onError: (_: any, { entityId, seasonId, fixtureId }: Props & { personId: string; data: any }) => {
      queryClient.invalidateQueries({
        queryKey: ["fixtureRoster", sport, organizationId, entityId, seasonId, fixtureId],
      });
    },
  });
};

export const useUpdateFixtureRosterPlayerDc = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId } = useParams();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();
  const { replaceRecords } = useRosterAndStaffQueryDataUpdater();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ entityId, seasonId, fixtureId, dcPersonId, data }: Props & { data: any; dcPersonId: string }) =>
      callApi(
        `/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/fixtures/${fixtureId}/roster/dc/${dcPersonId}`,
        {
          method: "PUT",
          body: data,
        }
      ),
    onSuccess: (response: any, { entityId, seasonId, fixtureId }: Props & { data: any; dcPersonId: string }) => {
      replaceRecords(["fixtureRoster", sport, organizationId, entityId, seasonId, fixtureId], response);
      queryClient.invalidateQueries({
        queryKey: ["fixture", sport, organizationId, entityId, seasonId, fixtureId],
      });
      showSuccessNotification(formatMessage({ id: "notification.rosterPlayer.updated" }));
    },
    onError: (_: any, { entityId, seasonId, fixtureId }: Props & { data: any; dcPersonId: string }) => {
      queryClient.invalidateQueries({
        queryKey: ["fixtureRoster", sport, organizationId, entityId, seasonId, fixtureId],
      });
    },
  });
};
